"use client";

import { useEffect } from "react";

/**
 * LOT 244 — Verrou de scroll global pour les modales overlay.
 *
 * Comportement :
 *  - Quand une modale est ouverte (`open=true`), `<html>` et `<body>`
 *    reçoivent `overflow: hidden` → impossible de scroller la page
 *    en arrière-plan, que ce soit avec la molette sur le backdrop
 *    ou via le scroll chaining quand on atteint le bout de la
 *    modale.
 *  - Compteur de modales empilées : si plusieurs modales sont
 *    ouvertes simultanément (cas rare mais possible), le verrou
 *    n'est relâché que quand la DERNIÈRE se ferme.
 *  - Restaure proprement les styles inline précédents.
 *
 * Le scroll INTERNE des modales reste OK : il faut juste s'assurer
 * que la zone scrollable a `overscroll-behavior: contain` (Tailwind :
 * `overscroll-contain`) pour empêcher le scroll chaining quand on
 * atteint le bord.
 *
 * Usage :
 *   useModalScrollLock(open);
 */

let lockCount = 0;
let prevHtmlOverflow: string | null = null;
let prevBodyOverflow: string | null = null;

export function useModalScrollLock(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;

    if (lockCount === 0) {
      prevHtmlOverflow = document.documentElement.style.overflow || "";
      prevBodyOverflow = document.body.style.overflow || "";
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    }
    lockCount += 1;

    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) {
        document.documentElement.style.overflow = prevHtmlOverflow ?? "";
        document.body.style.overflow = prevBodyOverflow ?? "";
        prevHtmlOverflow = null;
        prevBodyOverflow = null;
      }
    };
  }, [open]);
}
